import styled from 'styled-components';

import { BLOG_WRITE_ANIM, COLORS, STATUS_COLORS, BAR_COLORS, REDUCED_MOTION } from '../../../../theme/tokens';
import { Text } from '../../../../components/Text/Text';
import { BLOBS, blobDrift, gradientRing, ringLayer } from '../../generating.styles';

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/** Field-like surface hosting the toolbar + the ProseMirror content area. */
export const Frame = styled.div`
  border: 1px solid ${COLORS.panelBorder};
  border-radius: 12px;
  background: ${COLORS.surfaceInset};
  overflow: hidden;
`;

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 8px;
  border-bottom: 1px solid ${COLORS.hairline};
`;

export const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  & + &::before {
    content: '';
    width: 1px;
    height: 18px;
    margin: 0 6px;
    background: ${COLORS.hairline};
  }
`;

export const ToolButton = styled.button<{ $active?: boolean }>`
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 8px;
  background: ${(p) => (p.$active ? COLORS.surfaceMuted : 'transparent')};
  color: ${(p) => (p.$active ? COLORS.textBright : COLORS.icon)};
  font-size: 15px;
  cursor: pointer;

  &:hover {
    background: ${COLORS.surfaceMuted};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

/* ── The Lumio treatment ───────────────────────────────────────────────────
 *
 * Two controls in this editor do not edit what the author wrote, they ASK LUMIO
 * TO WRITE IT: the toolbar's quiz button and the selected quiz's regenerate CTA.
 * Both carry the same two marks — the assistant's own logo, and a border painted
 * with the palette the studio uses everywhere something is being written for you
 * (the blog list's generating row, the deployment strip). Nothing else in the
 * toolbar looks like this, which is the point: a button that spends a model call
 * should not be indistinguishable from one that toggles bold.
 */

/**
 * Toolbar button that hands the block to Lumio (the quiz). It keeps the quiz's
 * own glyph — it sits among the insert buttons and has to be findable by what it
 * inserts — and carries the gradient ring alone to say who fills it in.
 */
export const LumioToolButton = styled.button`
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  cursor: pointer;
  padding: 0;
  color: ${COLORS.icon};
  font-size: 15px;
  ${gradientRing(COLORS.surfaceInset)}

  &:hover:not(:disabled) {
    color: ${COLORS.textBright};
    --gen-surface: ${COLORS.surfaceMuted};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

/** The assistant's mark, at icon size, inside a Lumio control. */
export const LumioMark = styled.img`
  width: 16px;
  height: 16px;
  display: block;
`;

/** The selected quiz's "write it with Lumio" CTA: the same ring, with copy. */
export const LumioAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 10px;
  border-radius: 8px;
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  ${gradientRing(COLORS.surfaceInset)}

  &:hover:not(:disabled) {
    --gen-surface: ${COLORS.surfaceMuted};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/** Inline URL row revealed by the link tool. */
export const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-bottom: 1px solid ${COLORS.hairline};
`;

export const LinkInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: ${COLORS.text};
  font-size: 14px;
  font-family: inherit;

  &::placeholder {
    color: ${COLORS.textFaint};
  }
`;

export const LinkAction = styled.button`
  border: none;
  background: transparent;
  color: ${COLORS.ctaText};
  font-size: 13px;
  font-weight: 500;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${COLORS.surfaceMuted};
  }
`;

/** A refusal from the quiz writer, shown where the block being written is. */
export const ErrorRow = styled(Text)`
  display: block;
  padding: 8px 12px;
  border-bottom: 1px solid ${COLORS.hairline};
  font-size: 13px;
  line-height: 1.45;
  color: ${STATUS_COLORS.negative};
`;

/** Source editor for an engagement block, inside its dialog. */
export const SourceArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 180px;
  resize: vertical;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceInset};
  color: ${COLORS.text};
  font-family: monospace;
  font-size: 13px;
  line-height: 1.6;
  outline: none;

  &::placeholder {
    color: ${COLORS.textFaint};
  }
`;

export const SourceHint = styled(Text)`
  display: block;
  font-size: 12px;
  line-height: 1.5;
  color: ${COLORS.textFaint};
  margin-bottom: 8px;
`;

/* ── The generating animation ──────────────────────────────────────────────
 *
 * A quiz being written shows the same thing the blog list shows for an article
 * being written: a coloured arc travelling around the block's edge. Underneath
 * the source, out-of-focus colour drifts about, which is what says "this text is
 * about to be replaced" rather than "this text is loading".
 */

/**
 * The ProseMirror content area. Class-based styling because the DOM is
 * editor-owned; every value is a token. Callout tints mirror the preview:
 * tip = positive, warning = warning, note/key = accent.
 *
 * The area is a FIXED height the author sets with the handle below it, not one
 * that grows with the article: a long body used to push the SEO section and the
 * Save bar further down with every paragraph, so the further you wrote the more
 * scrolling everything else cost.
 */
export const Content = styled.div<{ $height: number }>`
  height: ${(p) => p.$height}px;
  overflow-y: auto;
  overscroll-behavior: contain;

  .ProseMirror {
    /* Fills the frame so a short article still offers the whole area to click
       into, and scrolls inside it once the body is longer than that. */
    min-height: 100%;
    box-sizing: border-box;
    padding: 16px;
    outline: none;
    color: ${COLORS.text};
    font-size: 15px;
    line-height: 1.65;

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      height: 0;
      pointer-events: none;
      color: ${COLORS.textFaint};
    }

    h2,
    h3 {
      color: ${COLORS.heading};
      line-height: 1.25;
      margin: 22px 0 8px;
    }

    h2 {
      font-size: 22px;
    }

    h3 {
      font-size: 18px;
    }

    p {
      margin: 8px 0;
    }

    ul,
    ol {
      padding-left: 22px;
      margin: 8px 0;
    }

    blockquote {
      margin: 12px 0;
      padding: 4px 14px;
      border-left: 2px solid ${COLORS.panelBorder};
      color: ${COLORS.textMuted};
    }

    code {
      background: ${COLORS.surfaceMuted};
      border-radius: 6px;
      padding: 1px 5px;
      font-size: 13px;
    }

    pre {
      background: ${COLORS.surfaceCard};
      border: 1px solid ${COLORS.panelBorder};
      border-radius: 10px;
      padding: 12px 14px;
      margin: 12px 0;
      overflow-x: auto;

      code {
        background: none;
        padding: 0;
      }
    }

    hr {
      border: none;
      border-top: 1px solid ${COLORS.hairline};
      margin: 20px 0;
    }

    img {
      max-width: 100%;
      border-radius: 10px;
      display: block;
      margin: 12px 0;
    }

    a {
      color: ${COLORS.ctaText};
      text-decoration: underline;
    }

    table {
      border-collapse: collapse;
      margin: 12px 0;
      width: 100%;
      table-layout: fixed;

      th,
      td {
        border: 1px solid ${COLORS.panelBorder};
        padding: 6px 10px;
        vertical-align: top;
        position: relative;
      }

      th {
        background: ${COLORS.surfaceFaint};
        color: ${COLORS.textBright};
        text-align: left;
        font-size: 13px;
      }

      .selectedCell::after {
        content: '';
        position: absolute;
        inset: 0;
        background: ${COLORS.surfaceMuted};
        pointer-events: none;
      }
    }

    aside[data-callout] {
      margin: 12px 0;
      padding: 12px 16px 12px 30px;
      border-radius: 12px;
      background: ${COLORS.surfaceFaint};
      position: relative;

      &::before {
        content: '';
        position: absolute;
        left: 14px;
        top: 19px;
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: ${COLORS.accent};
      }
    }

    aside[data-callout='tip']::before {
      background: ${STATUS_COLORS.positive};
    }

    aside[data-callout='warning']::before {
      background: ${BAR_COLORS.warning};
    }

    /*
     * The engagement blocks (::: toggle, ::: quiz, ::: infographic). They are ATOMS
     * holding their source verbatim (see ./dialectBlock), so what the author gets
     * is a card that shows the block's own fence line as its label and the source
     * beneath it. It is deliberately not styled to look like the rendered result:
     * this IS the source, and pretending otherwise would invite in-place edits the
     * node cannot accept.
     */
    div[data-dialect] {
      position: relative;
      /* Its own stacking context, so the generating layers below stay inside the
         card instead of resolving against the whole content area. */
      isolation: isolate;
      margin: 14px 0;
      padding: 28px 14px 12px;
      border-radius: 12px;
      border: 1px solid ${COLORS.panelBorder};
      background: ${COLORS.surfaceFaint};

      &::before {
        content: ':::' attr(data-dialect) ' ' attr(data-dialect-title);
        position: absolute;
        top: 8px;
        left: 14px;
        right: 14px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-family: monospace;
        font-size: 11px;
        letter-spacing: 0.02em;
        color: ${COLORS.textFaint};
      }

      pre {
        position: relative;
        isolation: isolate;
        margin: 0;
        padding: 0;
        border: none;
        background: none;
        white-space: pre-wrap;
        word-break: break-word;
        font-size: 13px;
        line-height: 1.6;
        color: ${COLORS.textMuted};
      }

      &.ProseMirror-selectednode {
        border-color: ${COLORS.ctaBorder};
        background: ${COLORS.surfaceMuted};
      }
    }

    /* Being written by Lumio: the arc travels around the card's rim… The
       layers are the page's shared ones (see ../../generating.styles), so a
       quiz being written looks like every other thing being written. */
    div[data-dialect][data-generating]::after {
      content: '';
      ${ringLayer}
    }

    /* …and colour drifts, heavily out of focus, BEHIND the source it is about to
       replace. Inset past the text box so the blur has room to fall off. */
    div[data-dialect][data-generating] pre::before {
      content: '';
      position: absolute;
      inset: -14px;
      z-index: -1;
      border-radius: 12px;
      background: ${BLOBS};
      filter: blur(26px);
      opacity: 0.45;
      pointer-events: none;
      animation: ${blobDrift} ${BLOG_WRITE_ANIM.driftMs}ms ease-in-out infinite;
    }

    div[data-dialect][data-generating] pre {
      /* The old source stays readable but recedes, so the card reads as being
         rewritten rather than as having lost its content. */
      opacity: 0.55;
    }

    ${REDUCED_MOTION} {
      div[data-dialect][data-generating]::after,
      div[data-dialect][data-generating] pre::before {
        animation: none;
      }
    }
  }
`;

/**
 * The grab bar under the content area. A bar across the bottom edge rather than
 * the browser's own `resize: vertical` corner grip: the grip lands bottom-RIGHT,
 * inside the scrollbar's lane, and cannot be given the app's own colours.
 *
 * It is a `separator` with a value, which is the pattern a window splitter
 * announces itself as, so the height is also settable from the keyboard (Arrow
 * Up/Down) by anyone who cannot drag.
 */
export const ResizeHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 14px;
  border-top: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
  cursor: ns-resize;
  /* Claim the gesture from the scroller, or a vertical drag on a phone scrolls
     the page instead of resizing the editor. */
  touch-action: none;

  &::before {
    content: '';
    width: 36px;
    height: 3px;
    border-radius: 99px;
    background: ${COLORS.panelBorder};
  }

  &:hover::before,
  &:focus-visible::before {
    background: ${COLORS.ctaBorder};
  }

  &:focus-visible {
    outline: none;
    background: ${COLORS.surfaceMuted};
  }
`;
